import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SelectInput,
  ApiErrorText,
  SearchableSelect,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  IconTextItem,
  Loader,
  Textarea,
  AnchorDropdown,
  Button,
  AssetBaseInfo,
} from '@/shared/components';
import {
  DEFAULT_BASE_ASSET,
  DEFAULT_CRYPTO_ADDRESS_PATTERN,
} from '@/shared/config';
import {
  createRegexTextareaHandlers,
  formatReceivedAmount,
  formatReceivedPrice,
  getRequiredLabel,
} from '@/shared/lib';
import type {
  AssetOptionWithBalance,
  NetworkOption,
  WithdrawCryptoFormValues,
} from '../model';
import ContactBookIcon from '@/shared/assets/contact-book-icon.svg?react';
import type { UseFormReturn } from 'react-hook-form';
import type { CommissionResponse } from '@/shared/types';
import {
  WithdrawAmountFormField,
  WithdrawIncludeFeeFormField,
} from '@/entities/operation';
import type { SavedCryptoWalletAddress } from '@/entities/crypto-address';
import EditIcon from '@/shared/assets/edit-icon.svg?react';

interface WithdrawCryptoFormProps {
  form: UseFormReturn<WithdrawCryptoFormValues>;
  watchedValues: WithdrawCryptoFormValues;
  assetOptions: AssetOptionWithBalance[];
  availableNetworks: NetworkOption[];
  selectedAsset?: AssetOptionWithBalance;
  selectedNetwork?: NetworkOption;
  minWithdraw?: number;
  hasWithdrawFee: boolean;
  isLoading: boolean;
  isError: boolean;
  error?: unknown | null;
  onMaxClick: () => void;
  onFormDirtyChange: (isDirty: boolean) => void;
  commissionData?: CommissionResponse;
  savedCryptoAddresses: SavedCryptoWalletAddress[];
  onEditSavedCryptoAddress: (
    savedCryptoAddress: SavedCryptoWalletAddress
  ) => void;
  setTemplateId: Dispatch<SetStateAction<string | undefined>>;
}

export function WithdrawCryptoForm({
  form,
  assetOptions,
  availableNetworks,
  selectedAsset,
  selectedNetwork,
  minWithdraw,
  hasWithdrawFee,
  isLoading,
  isError,
  error,
  onMaxClick,
  onFormDirtyChange,
  watchedValues,
  savedCryptoAddresses,
  onEditSavedCryptoAddress,
  setTemplateId,
}: WithdrawCryptoFormProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const formValues = form.getValues();
    const hasAnyFieldFilled = Object.values(formValues).some(value => {
      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      return value;
    });

    onFormDirtyChange(hasAnyFieldFilled);
  }, [watchedValues, onFormDirtyChange]);

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ApiErrorText error={error} size="small" />;
  }

  const networksDisabled = availableNetworks.length <= 1;

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <Form {...form}>
        <form className="flex flex-col gap-[1.6rem]">
          <FormField
            control={form.control}
            name="assetCode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <SearchableSelect<AssetOptionWithBalance>
                    {...field}
                    value={field.value}
                    onValueChange={value => {
                      field.onChange(value);
                      form.setValue('cryptoAddress', '');
                      setTemplateId('');
                      form.clearErrors('cryptoAddress');
                    }}
                    floatingLabel={getRequiredLabel(
                      t('withdrawMethods.crypto.assetLabel')
                    )}
                    searchFilter={(option, search) =>
                      option.code
                        .toLowerCase()
                        .indexOf(search.toLowerCase()) !== -1
                    }
                    items={assetOptions}
                    renderSelectedValue={value => {
                      const asset = assetOptions.find(
                        asset => asset.value === value
                      );

                      if (!asset) return <></>;

                      return (
                        <div className="flex items-center justify-between w-full">
                          <IconTextItem
                            icon={asset.icon}
                            title={asset.code}
                            variant="compact"
                          />
                        </div>
                      );
                    }}
                    renderItem={option => {
                      const asset = option as AssetOptionWithBalance;

                      return (
                        <div className="flex items-center justify-between w-full">
                          <IconTextItem {...asset} title={asset.code} />
                          <div className="flex flex-col items-end">
                            <div className="flex items-center gap-1">
                              <div className="headline4">
                                {formatReceivedAmount(asset.available)}
                              </div>
                            </div>
                            {asset?.baseAvailable &&
                              asset.code !== DEFAULT_BASE_ASSET && (
                                <div className="flex items-center p5 text-muted-foreground">
                                  {`${formatReceivedPrice(asset?.baseAvailable || 0)} ${DEFAULT_BASE_ASSET}`}
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-[0.4rem]">
            <FormField
              control={form.control}
              name="networkName"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  value={selectedNetwork?.code}
                  floatingLabel={getRequiredLabel(
                    t('withdrawMethods.crypto.networkLabel')
                  )}
                  renderSelected={option => {
                    const selectedOption = option as NetworkOption;

                    return (
                      <div className="p3">
                        {selectedOption.name} {selectedOption.code}
                      </div>
                    );
                  }}
                  options={availableNetworks}
                  valueKey="code"
                  labelKey="name"
                  selectedKey="code"
                  disabled={networksDisabled}
                />
              )}
            />
          </div>

          {/* Crypto Address Input */}
          <FormField
            control={form.control}
            name="cryptoAddress"
            render={({ field }) => {
              const addressHandlers = createRegexTextareaHandlers(
                new RegExp(`^[${DEFAULT_CRYPTO_ADDRESS_PATTERN}]+$`),
                field.onChange
              );

              return (
                <FormItem>
                  <FormControl>
                    <AnchorDropdown<SavedCryptoWalletAddress>
                      triggerIcon={
                        <ContactBookIcon className="size-[2.4rem]" />
                      }
                      disabledTrigger={!watchedValues.assetCode}
                      options={savedCryptoAddresses ?? []}
                      getOptionKey={o => o.templateId}
                      contentClassName="max-h-[28.8rem]"
                      renderOption={o => (
                        <div className="py-[1.4rem] px-[2.4rem] flex items-center justify-between min-h-[7.2rem] rounded-[1.6rem] hover:bg-secondary-bg">
                          <AssetBaseInfo
                            asset={{
                              icon: o.networkIcon,
                              code: o.templateName,
                              name: o.networkName + ' ' + (o.networkCode ?? ''),
                            }}
                            className="max-w-[calc(100%-1.6rem-4.4rem)]"
                          />

                          <Button
                            variant="icon"
                            size="icon-button"
                            className="w-[4.4rem] h-[4.4rem] hover:bg-secondary-bg text-tertiary hover:text-primary"
                            onClick={() => onEditSavedCryptoAddress(o)}>
                            <EditIcon className="size-[2.4rem]" />
                          </Button>
                        </div>
                      )}
                      onSelect={option => {
                        setTemplateId(option.templateId);
                        form.setValue('cryptoAddress', option.cryptoAddress);
                      }}>
                      {trigger => (
                        <Textarea
                          {...field}
                          label={getRequiredLabel(
                            t('withdrawMethods.crypto.addressPlaceholder')
                          )}
                          className="p3"
                          autoGrow
                          onBeforeInput={addressHandlers.beforeInput}
                          onPaste={e => {
                            setTemplateId('');
                            addressHandlers.paste(e);
                          }}
                          onChange={e => {
                            setTemplateId('');
                            addressHandlers.change(e);
                          }}
                          onKeyDown={e => {
                            if (e.key === ' ') e.preventDefault();
                          }}
                          rightContent={trigger}
                        />
                      )}
                    </AnchorDropdown>
                  </FormControl>
                  <FormMessage className="px-[1.6rem]" />

                  {field.value && selectedNetwork && (
                    <div className="p5 text-muted-foreground px-[1.6rem]">
                      {t('withdrawMethods.crypto.addressWarning')}
                      <span className="headline6 text-foreground pl-[0.3rem]">
                        ({selectedNetwork.name} {selectedNetwork.code})
                      </span>
                    </div>
                  )}
                </FormItem>
              );
            }}
          />

          <WithdrawAmountFormField
            control={form.control}
            name="amount"
            selectedAsset={selectedAsset}
            minAmount={minWithdraw}
            onMaxClick={onMaxClick}
          />

          <WithdrawIncludeFeeFormField
            control={form.control}
            name="includeFee"
            visible={hasWithdrawFee}
          />
        </form>
      </Form>
    </div>
  );
}
